import moment from "moment-timezone/builds/moment-timezone-with-data-10-year-range.js";

import { t } from "@/localization";

export const getEventExpiryMoment = (event) => {
  if (!event) return null;

  const expiryFromEvent = event.datetime_expiry
    ? moment(event.datetime_expiry)
    : null;

  if (expiryFromEvent?.isValid()) return expiryFromEvent;

  if (event.event_datetime_utc) {
    const fallbackExpiry = moment(event.event_datetime_utc)
      .add(1, "years")
      .add(1, "day");
    if (fallbackExpiry.isValid()) return fallbackExpiry;
  }

  return null;
};

const hasText = (value) => typeof value === "string" && value.trim().length > 0;

// True once there is something to keep online: a live broadcast (putEventLive
// sets datetime_broadcast_started; "-TEST" streams do not) or an uploaded video.
export const hasEventReplay = (event) =>
  !!event &&
  (hasText(event.datetime_broadcast_started) || event.video_uploaded === true);

// The event's scheduled window (start - 1h ... start + 24h x days + 1h) is over.
// Until then the event can still happen, even when event_status is already
// "past": putEventPaused sets "past" on every stream stop, including a quick
// test on the real event before the ceremony.
export const hasEventWindowEnded = (event, now = moment()) => {
  const windowEnd =
    event?.event_window_end_datetime_utc || event?.event_datetime_utc;
  if (!windowEnd) return true;

  const windowEndMoment = moment(windowEnd);
  return !windowEndMoment.isValid() || now.isAfter(windowEndMoment);
};

// Expiry countdowns, "Expiring soon" and Extend CTAs only make sense for an
// event that is over and has a replay to keep.
export const shouldShowStorageExpiry = (event, now = moment()) =>
  !!event &&
  event.is_test !== true &&
  event.event_status === "past" &&
  hasEventReplay(event) &&
  hasEventWindowEnded(event, now);

export const isEventArchived = (event, accountTypeOverride, now = moment()) => {
  if (!event) return false;

  // Test events are never archived.
  if (event.is_test === true) return false;

  // Explicit archived flag is an override when available.
  if (event.archived === true) return true;
  if (event.archived === false) return false;

  const accountType = event.profile?.account_type || accountTypeOverride;
  if (accountType === "business") return false;

  if (event.event_status === "archived") return true;

  // Nothing is in the archive for an event that never streamed (or uploaded a
  // video), and an event that hasn't happened yet can't have expired, e.g.
  // when it was moved to a date after its stored datetime_expiry.
  if (!hasEventReplay(event) || !hasEventWindowEnded(event, now)) return false;

  const expiryMoment = getEventExpiryMoment(event);
  if (!expiryMoment) return false;

  return now.isAfter(expiryMoment);
};

export const getExpiryUrgency = (expiryMoment, now = moment()) => {
  if (!expiryMoment) return "normal";

  const daysLeft = expiryMoment.diff(now, "days", true);
  if (daysLeft <= 0) return "expired";
  if (daysLeft <= 3) return "critical";
  if (daysLeft <= 14) return "warning";

  return "normal";
};

export const formatEventExpiryDate = (expiryMoment, timezone) => {
  if (!expiryMoment || !expiryMoment.isValid()) return null;

  const date = timezone
    ? expiryMoment.clone().tz(timezone)
    : expiryMoment.clone().local();

  return date.format(t("MMM D, YYYY"));
};
